import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { settingsAPI } from '../lib/api';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { Download, RotateCcw, Loader2 } from 'lucide-react';

const THEME_COLORS_STORAGE_KEY = 'appThemeColors';

function persistThemeColors(lightThemeColor, darkThemeColor) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(
      THEME_COLORS_STORAGE_KEY,
      JSON.stringify({ lightThemeColor, darkThemeColor })
    );
  } catch {
    // ignore
  }
  try {
    window.dispatchEvent(
      new CustomEvent('appThemeColorsUpdated', {
        detail: { lightThemeColor, darkThemeColor },
      })
    );
  } catch {
    // ignore
  }
}

export default function ApplicationSettings() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({
    contractEndReminder: 'three_months',
    reminderEmailPeriod: 'weekly',
    backupEmailPeriod: 'monthly',
    email: '',
    email2: '',
    sidebar: 'modern',
    lightThemeColor: '#333333',
    darkThemeColor: '#4b5563',
  });
  const [includeFiles, setIncludeFiles] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupProgress, setBackupProgress] = useState(0);
  const [backupDeterminate, setBackupDeterminate] = useState(false);

  useEffect(() => {
    // Load settings from backend
    const loadSettings = async () => {
      try {
        const response = await settingsAPI.get();
        if (response.data) {
          const data = response.data;
          setSettings((prev) => {
            const next = {
              ...prev,
              contractEndReminder: data.contract_end_reminder || 'three_months',
              reminderEmailPeriod: data.reminder_email_period || 'weekly',
              backupEmailPeriod: data.backup_email_period || 'monthly',
              email: data.email || '',
              email2: data.email2 || '',
              sidebar: data.sidebar || 'modern',
              lightThemeColor: data.light_theme_color || '#333333',
              darkThemeColor: data.dark_theme_color || '#4b5563',
            };
            // Apply immediately so Layout doesn't flash old/default color on navigation
            persistThemeColors(next.lightThemeColor, next.darkThemeColor);
            return next;
          });
        }
      } catch (error) {
        console.error('Failed to load settings', error);
        toast.error(t('appSettings.loadError'));
      }
    };
    loadSettings();
  }, []);

  const handleChange = (key, value) => {
    setSettings(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'lightThemeColor' || key === 'darkThemeColor') {
        persistThemeColors(next.lightThemeColor, next.darkThemeColor);
      }
      return next;
    });
  };

  const handleSave = async () => {
    try {
      const payload = {
        contract_end_reminder: settings.contractEndReminder,
        reminder_email_period: settings.reminderEmailPeriod,
        backup_email_period: settings.backupEmailPeriod,
        email: settings.email,
        email2: settings.email2,
        sidebar: settings.sidebar,
        light_theme_color: settings.lightThemeColor,
        dark_theme_color: settings.darkThemeColor,
      };
      await settingsAPI.update(payload);
      toast.success(t('appSettings.saveSuccess'));
    } catch (error) {
      console.error('Failed to save settings', error);
      toast.error(t('appSettings.saveError'));
    }
  };

  const handleReset = async () => {
    const defaultSettings = {
      contractEndReminder: 'three_months',
      reminderEmailPeriod: 'weekly',
      backupEmailPeriod: 'monthly',
      email: '',
      email2: '',
      sidebar: 'modern',
      lightThemeColor: '#333333',
      darkThemeColor: '#4b5563',
    };
    try {
      setSettings(defaultSettings);
      persistThemeColors(defaultSettings.lightThemeColor, defaultSettings.darkThemeColor);
      const payload = {
        contract_end_reminder: defaultSettings.contractEndReminder,
        reminder_email_period: defaultSettings.reminderEmailPeriod,
        backup_email_period: defaultSettings.backupEmailPeriod,
        email: defaultSettings.email,
        email2: defaultSettings.email2,
        sidebar: defaultSettings.sidebar,
        light_theme_color: defaultSettings.lightThemeColor,
        dark_theme_color: defaultSettings.darkThemeColor,
      };
      await settingsAPI.update(payload);
      toast.success(t('appSettings.resetSuccess'));
    } catch (error) {
      toast.error(t('appSettings.resetError'));
    }
  };

  const handleBackupDownload = () => {
    let trickle = null;
    const download = async () => {
      setBackupLoading(true);
      setBackupProgress(0);
      setBackupDeterminate(false);
      // Sunucu zipleme süresi belirsiz -> bar yavaşça ~%90'a kadar ilerlesin (trickle).
      trickle = setInterval(() => {
        setBackupProgress((p) => (p < 90 ? Math.min(90, p + Math.max(1, (90 - p) / 12)) : p));
      }, 400);
      try {
        const res = await settingsAPI.downloadBackup(includeFiles, (evt) => {
          // İndirme başlayınca gerçek yüzdeye geç (Content-Length biliniyorsa).
          if (evt && evt.total) {
            if (trickle) { clearInterval(trickle); trickle = null; }
            setBackupDeterminate(true);
            setBackupProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        });
        if (trickle) { clearInterval(trickle); trickle = null; }
        setBackupProgress(100);
        const blob = new Blob([res.data], { type: 'application/zip' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const suffix = includeFiles ? '-with-files' : '';
        link.download = `contract-manager-backup${suffix}-${new Date().toISOString().split('T')[0]}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success(t('appSettings.backupDownloaded'));
      } catch (error) {
        console.error('Backup download failed', error);
        toast.error(t('appSettings.saveError'));
      } finally {
        if (trickle) clearInterval(trickle);
        // Kısa bir an %100 göster, sonra gizle.
        setTimeout(() => {
          setBackupLoading(false);
          setBackupProgress(0);
          setBackupDeterminate(false);
        }, 600);
      }
    };
    download();
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="application-settings-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight dark:text-white">{t('appSettings.title')}</h1>
          <p className="text-muted-foreground dark:text-gray-400 mt-1">
            {t('appSettings.subtitle')}
          </p>
        </div>

        {/* Settings Form */}
        <Card className="dark:bg-[#333333] dark:border-zinc-800 overflow-hidden">
          <CardContent className="pt-6 space-y-6">
            {/* Contract End Reminder */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <Label className="text-sm font-medium dark:text-white">{t('appSettings.contractEndReminder')}</Label>
              <Select
                value={settings.contractEndReminder}
                onValueChange={(value) => handleChange('contractEndReminder', value)}
              >
                <SelectTrigger data-testid="contract-end-select" className="dark:bg-[#444444] dark:border-gray-700 dark:text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark:bg-[#444444] dark:border-gray-700">
                  <SelectItem value="one_day" className="dark:text-white dark:focus:bg-[#555555]">{t('appSettings.contractEndReminderOptions.one_day')}</SelectItem>
                  <SelectItem value="one_week">{t('appSettings.contractEndReminderOptions.one_week')}</SelectItem>
                  <SelectItem value="one_month">{t('appSettings.contractEndReminderOptions.one_month')}</SelectItem>
                  <SelectItem value="three_months">{t('appSettings.contractEndReminderOptions.three_months')}</SelectItem>
                  <SelectItem value="six_months">{t('appSettings.contractEndReminderOptions.six_months')}</SelectItem>
                  <SelectItem value="one_year">{t('appSettings.contractEndReminderOptions.one_year')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Reminder Email Period */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <Label className="text-sm font-medium dark:text-white">{t('appSettings.reminderEmailPeriod')}</Label>
              <Select
                value={settings.reminderEmailPeriod}
                onValueChange={(value) => handleChange('reminderEmailPeriod', value)}
              >
                <SelectTrigger data-testid="reminder-period-select" className="dark:bg-[#444444] dark:border-gray-700 dark:text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark:bg-[#444444] dark:border-gray-700">
                  <SelectItem value="daily" className="dark:text-white dark:focus:bg-[#555555]">{t('appSettings.periodOptions.daily')}</SelectItem>
                  <SelectItem value="weekly">{t('appSettings.periodOptions.weekly')}</SelectItem>
                  <SelectItem value="monthly">{t('appSettings.periodOptions.monthly')}</SelectItem>
                  <SelectItem value="quarterly">{t('appSettings.periodOptions.quarterly')}</SelectItem>
                  <SelectItem value="half_yearly">{t('appSettings.periodOptions.half_yearly')}</SelectItem>
                  <SelectItem value="yearly">{t('appSettings.periodOptions.yearly')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Backup Email Period */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <Label className="text-sm font-medium dark:text-white">{t('appSettings.backupEmailPeriod')}</Label>
              <Select
                value={settings.backupEmailPeriod}
                onValueChange={(value) => handleChange('backupEmailPeriod', value)}
              >
                <SelectTrigger data-testid="backup-period-select" className="dark:bg-[#444444] dark:border-gray-700 dark:text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark:bg-[#444444] dark:border-gray-700">
                  <SelectItem value="daily" className="dark:text-white dark:focus:bg-[#555555]">{t('appSettings.periodOptions.daily')}</SelectItem>
                  <SelectItem value="weekly">{t('appSettings.periodOptions.weekly')}</SelectItem>
                  <SelectItem value="monthly">{t('appSettings.periodOptions.monthly')}</SelectItem>
                  <SelectItem value="quarterly">{t('appSettings.periodOptions.quarterly')}</SelectItem>
                  <SelectItem value="half_yearly">{t('appSettings.periodOptions.half_yearly')}</SelectItem>
                  <SelectItem value="yearly">{t('appSettings.periodOptions.yearly')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <Label className="text-sm font-medium dark:text-white">{t('appSettings.email')}</Label>
              <Input
                type="email"
                value={settings.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder={t('appSettings.emailPlaceholder')}
                data-testid="email-input"
                className="dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500"
              />
            </div>

            {/* Email 2 (opsiyonel ikinci alıcı) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <Label className="text-sm font-medium dark:text-white">{t('appSettings.email2')}</Label>
              <Input
                type="email"
                value={settings.email2}
                onChange={(e) => handleChange('email2', e.target.value)}
                placeholder={t('appSettings.email2Placeholder')}
                data-testid="email2-input"
                className="dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500"
              />
            </div>

            {/* Sidebar */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <Label className="text-sm font-medium dark:text-white">{t('appSettings.sidebar')}</Label>
              <Select
                value={settings.sidebar}
                onValueChange={(value) => handleChange('sidebar', value)}
              >
                <SelectTrigger data-testid="sidebar-select" className="dark:bg-[#444444] dark:border-gray-700 dark:text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark:bg-[#444444] dark:border-gray-700">
                  <SelectItem value="modern" className="dark:text-white dark:focus:bg-[#555555]">{t('appSettings.sidebarOptions.modern')}</SelectItem>
                  <SelectItem value="classic">{t('appSettings.sidebarOptions.classic')}</SelectItem>
                  <SelectItem value="minimal">{t('appSettings.sidebarOptions.minimal')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Light Theme Color */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <Label className="text-sm font-medium">{t('appSettings.themeColorLight')}</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={settings.lightThemeColor}
                  onChange={(e) => handleChange('lightThemeColor', e.target.value)}
                  className="w-12 h-10 rounded cursor-pointer border-0"
                  data-testid="light-theme-color"
                />
                <span className="text-sm text-muted-foreground dark:text-gray-400 font-mono">{settings.lightThemeColor}</span>
              </div>
            </div>

            {/* Dark Theme Color */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <Label className="text-sm font-medium">{t('appSettings.themeColorDark')}</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={settings.darkThemeColor}
                  onChange={(e) => handleChange('darkThemeColor', e.target.value)}
                  className="w-12 h-10 rounded cursor-pointer border-0"
                  data-testid="dark-theme-color"
                />
                <span className="text-sm text-muted-foreground dark:text-gray-400 font-mono">{settings.darkThemeColor}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
              <Button
                variant="outline"
                onClick={handleReset}
                data-testid="reset-btn"
                className="dark:bg-gray-700 dark:hover:bg-gray-600 dark:border-gray-700 dark:text-white"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                {t('appSettings.resetDefaults')}
              </Button>
              <Button
                onClick={handleSave}
                data-testid="save-btn"
                className="dark:bg-zinc-700 dark:hover:bg-zinc-600 dark:text-white"
              >
                {t('appSettings.saveChanges')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Backup Section */}
        <Card className="dark:bg-[#333333] dark:border-zinc-800 overflow-hidden">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <div>
                <Label className="text-sm font-medium dark:text-white">{t('appSettings.downloadBackup')}</Label>
                <p className="text-sm text-muted-foreground dark:text-gray-400 mt-1">
                  {t('appSettings.downloadBackupDescription')}
                </p>
              </div>
              <div className="flex flex-col gap-3 items-start">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="include-files"
                    checked={includeFiles}
                    onCheckedChange={(v) => setIncludeFiles(v === true)}
                    data-testid="include-files-checkbox"
                    className="dark:border-gray-600"
                  />
                  <Label htmlFor="include-files" className="text-sm dark:text-gray-300 cursor-pointer">
                    {t('appSettings.includeContractFiles', { defaultValue: 'Sözleşme dosyalarını da indir (PDF)' })}
                  </Label>
                </div>
                <Button
                  variant="default"
                  onClick={handleBackupDownload}
                  data-testid="backup-btn"
                  disabled={backupLoading}
                >
                  {backupLoading
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <Download className="w-4 h-4 mr-2" />}
                  {t('appSettings.download')}
                </Button>

                {backupLoading && (
                  <div className="w-full max-w-sm mt-1 space-y-1.5" data-testid="backup-progress">
                    <Progress value={backupProgress} className="h-2" />
                    <p className="text-xs text-muted-foreground dark:text-gray-400 flex items-center gap-1.5">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      {backupDeterminate
                        ? `${t('appSettings.backupDownloading', { defaultValue: 'İndiriliyor' })} %${backupProgress}`
                        : t('appSettings.backupPreparing', { defaultValue: 'Yedek hazırlanıyor, lütfen bekleyin…' })}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
